import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import Checkbox from "PFComponents/checkbox/checkbox";
import PageCard from "PFComponents/page/page_card";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import cssInputFieldSet from "PFComponents/text/input_field_set/input_field_set.module.scss";
import { GROWL_ACTIONS } from "PFReducers/growl_reducer";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import SaasAdminHeader from "../shared/saas_admin_header";
import { AccountCustomization } from "./account_customization";
import { CustomLinks } from "./components/custom_links";
import { MarkdownInputWithPreview } from "./components/markdown_input_with_preview";
import css from "./customization_page.less";

const URL_REGEX =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

// based on regex from https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
// with modified domain match requirement from "*" to "+" to prevent (valid) emails like: test@localhost
const MAIL_TO_REGEX =
  /^mailto:[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

type CustomLinkError = { url?: string; title?: string };

type CustomizationPageProps = {
  customization: AccountCustomization;
  accountName: string;
  putCustomization: (data: any) => Promise<boolean>;
  saving: boolean;
};

const CustomizationPage = ({
  accountName,
  customization,
  putCustomization,
  saving
}: CustomizationPageProps) => {
  const { t } = useTranslation(["saasAdmin", "admin"]);

  const { dispatch } = useContext(AppContext);
  const [formErrors, setFormErrors] = useState<{ customLinks?: CustomLinkError[] }>({});

  const [linkedinUrl, setLinkedInUrl] = useState(customization.linkedinUrl);
  const [facebookUrl, setFacebookUrl] = useState(customization.facebookUrl);
  const [twitterUrl, setTwitterUrl] = useState(customization.twitterUrl);
  const [instagramUrl, setInstagramUrl] = useState(customization.instagramUrl);
  const [copyright, setCopyright] = useState(customization.copyright);
  const [contactInfo, setContactInfo] = useState(customization.contactInfo);
  const [footerLinks, setFooterLinks] = useState(customization.footerLinks);
  const [showFooterLogo, setShowFooterLogo] = useState(customization.showFooterLogo);
  const [coverImage, setCoverImage] = useState<File>(customization.landingCover);
  const [landingDescription, setLandingDescription] = useState(customization.landingDescription);
  const [termsAndConditions, setTermsAndConditions] = useState(customization.termsAndConditions);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(customization.showTermsAndConditions);
  const [customLinks, setCustomLinks] = useState(customization.customLinks || []);

  const validateCustomLinks = () => {
    const errors: CustomLinkError[] = [];
    let hasError = false;

    customLinks.forEach((customLink, index) => {
      errors[index] = {};

      if (customLink.title === "" || customLink.title === null || customLink.title === undefined) {
        errors[index].title = "Must have a value";
        hasError = true;
      }

      if (customLink.url === "" || customLink.url === null || customLink.url === undefined) {
        errors[index].url = "Must have a value";
        hasError = true;
      } else {
        if (!customLink.url.match(URL_REGEX) && !customLink.url.match(MAIL_TO_REGEX)) {
          errors[index].url = t("saasAdmin:customization.customLinks.errors.invalidUrlOrEmail");
          hasError = true;
        }
      }
    });

    setFormErrors({ customLinks: errors });

    return !hasError;
  };

  const handleSave = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault();

    if (validateCustomLinks()) {
      const data = {
        linkedin_url: linkedinUrl,
        facebook_url: facebookUrl,
        twitter_url: twitterUrl,
        instagram_url: instagramUrl,
        copyright,
        contact_info: contactInfo,
        footer_links: footerLinks || "",
        show_footer_logo: showFooterLogo,
        landing_description: landingDescription || "",
        terms_and_conditions: termsAndConditions || "",
        show_terms_and_conditions: showTermsAndConditions,
        custom_links: customLinksToPayload(),
        landing_cover: coverImage
      };

      putCustomization(data).then((saved) => {
        if (saved) {
          dispatch({
            type: GROWL_ACTIONS.GROWL_RENDER,
            payload: {
              message: "Saved customization",
              kind: "success"
            }
          });
        }
      });
    } else {
      dispatch({
        type: GROWL_ACTIONS.GROWL_RENDER,
        payload: {
          message: "Failed to save, check the form for errors",
          kind: "error"
        }
      });
    }
  };

  const customLinksToPayload = () =>
    JSON.stringify(customLinks.map((customLink) => ({ title: customLink.title, url: customLink.url })));

  return (
    <div className={classNames(css.customizationPage, { [css.formingSaving]: saving })}>
      <SaasAdminHeader title="Customization" subTitle={`(for ${accountName})`} />
      <PageCard
        style={{
          marginTop: "2rem",
          marginBottom: "2rem"
        }}
      >
        <CustomLinks
          customLinks={customLinks}
          onCustomLinksUpdate={setCustomLinks}
          formErrors={formErrors.customLinks || []}
        />
        <h2>{t("saasAdmin:customization.footer")}</h2>

        <div className={css.inputs}>
          <InputFieldSet
            onChange={setLinkedInUrl}
            label={t("saasAdmin:customization.linkedinUrl")}
            value={linkedinUrl}
            tip={t("saasAdmin:leaveEmptyToHide")}
          />
          <InputFieldSet
            label={t("saasAdmin:customization.facebookUrl")}
            onChange={setFacebookUrl}
            value={facebookUrl}
            tip={t("saasAdmin:leaveEmptyToHide")}
          />
          <InputFieldSet
            label={t("saasAdmin:customization.twitterUrl")}
            onChange={setTwitterUrl}
            value={twitterUrl}
            tip={t("saasAdmin:leaveEmptyToHide")}
          />
          <InputFieldSet
            label={t("saasAdmin:customization.instagramUrl")}
            onChange={setInstagramUrl}
            value={instagramUrl}
            tip={t("saasAdmin:leaveEmptyToHide")}
          />
          <InputFieldSet
            label={t("saasAdmin:customization.copyright")}
            onChange={setCopyright}
            value={copyright}
            tip={t("saasAdmin:leaveEmptyToHide")}
          />
          <InputFieldSet
            label={t("saasAdmin:customization.contactInfo")}
            onChange={setContactInfo}
            value={contactInfo}
          />
        </div>
        <MarkdownInputWithPreview
          value={footerLinks}
          setValue={setFooterLinks}
          label={t("saasAdmin:customization.footerLinks")}
        />
        <Checkbox
          label={t("saasAdmin:customization.showFooterLogo")}
          handleChange={(val) => setShowFooterLogo(val)}
          checked={showFooterLogo}
        />

        <hr />

        <h2>{t("saasAdmin:customization.landing")}</h2>
        <div className={css.coverImage}>
          <label htmlFor="cover_image">{t("saasAdmin:customization.coverImage")}</label>
          <Button kind="primary" tag="a">
            Select image
          </Button>
        </div>
        <div className={css.imageField}>
          <input
            id="cover_image"
            type="file"
            style={{ display: "none" }}
            multiple={false}
            onChange={(event) => {
              setCoverImage(event.target.files![0]);
            }}
          />
          <div className={cssInputFieldSet.tip}>{t("saasAdmin:customization.coverTip")}</div>
          {coverImage && <img alt="Cover" src={URL.createObjectURL(coverImage)} />}
        </div>
        <MarkdownInputWithPreview
          value={landingDescription}
          setValue={setLandingDescription}
          label={t("saasAdmin:customization.description")}
        />
        <MarkdownInputWithPreview
          value={termsAndConditions}
          setValue={setTermsAndConditions}
          label={t("saasAdmin:customization.termsAndConditions")}
        />
        <Checkbox
          label={t("saasAdmin:customization.showTermsAndConditions")}
          handleChange={setShowTermsAndConditions}
          checked={showTermsAndConditions}
        />
        <div className={css.formActions}>
          <ButtonLink kind="" href="/accounts">
            {t("admin:form.buttons.cancel")}
          </ButtonLink>
          <Button type="submit" onClick={handleSave} disabled={saving}>
            {t("admin:form.buttons.save")}
          </Button>
        </div>
      </PageCard>
    </div>
  );
};

export default CustomizationPage;
